import type { CertificateTemplateSelectedFormField } from '../../types';

/** A single content block placed on a certificate page. */
export type CertificateBlock =
  | { kind: 'intro' }
  | { kind: 'equipTitle' }
  | { kind: 'equipCard' }
  | { kind: 'fields'; fields: CertificateTemplateSelectedFormField[] };

/** A block paired with its (fixed or measured) rendered height. */
export interface CertificateBlockInput {
  block: CertificateBlock;
  height: number;
}

export interface CertificatePageModel {
  blocks: CertificateBlock[];
  /** True on the page that carries the signature/footer block (the last one). */
  showSignatures: boolean;
}

export interface CertificatePaginationOptions {
  /** Content height available per page (below the repeated logo header). */
  capacity: number;
  /** Vertical gap between stacked blocks. */
  gap: number;
  /** Height of the signature + footer block. */
  signaturesHeight: number;
}

/** Group form fields into cards of up to `size` subfields each. */
export const chunkFields = (
  fields: CertificateTemplateSelectedFormField[],
  size: number
): CertificateTemplateSelectedFormField[][] => {
  const cards: CertificateTemplateSelectedFormField[][] = [];
  for (let i = 0; i < fields.length; i += size) {
    cards.push(fields.slice(i, i + size));
  }
  return cards;
};

/**
 * Distribute the certificate content across one or more fixed-height A4 pages.
 *
 * Every block (intro, equipment title, equipment card, and each fixed-height
 * form-fields card) is atomic: it is placed whole on the current page, or moved
 * to a fresh page when it doesn't fit. The signature/footer block lands on the
 * last page if it fits, otherwise on a dedicated extra page. The logo header
 * repeats on every page (its height is already excluded from `capacity`).
 */
export const buildCertificatePages = (
  blocks: CertificateBlockInput[],
  { capacity, gap, signaturesHeight }: CertificatePaginationOptions
): CertificatePageModel[] => {
  const pages: { blocks: CertificateBlock[]; used: number }[] = [];
  let page: { blocks: CertificateBlock[]; used: number } = {
    blocks: [],
    used: 0,
  };

  for (const { block, height } of blocks) {
    const add = (page.blocks.length ? gap : 0) + height;
    if (page.blocks.length && page.used + add > capacity) {
      pages.push(page);
      page = { blocks: [], used: 0 };
    }
    page.blocks.push(block);
    page.used += (page.blocks.length > 1 ? gap : 0) + height;
  }

  pages.push(page);

  // Place the signature/footer block on the last page if it fits there.
  let signaturesPage = pages.length - 1;
  const last = pages[signaturesPage];
  if (last && last.used + gap + signaturesHeight > capacity) {
    pages.push({ blocks: [], used: 0 });
    signaturesPage = pages.length - 1;
  }

  return pages.map((p, idx) => ({
    blocks: p.blocks,
    showSignatures: idx === signaturesPage,
  }));
};
